import {
  StandalonePrereqProps,
  StandalonePrereqViewProps,
} from './StandalonePrereq.types'
import { PrereqStep, useFormForStep } from 'context/FlowPrerequirements'
import { FormQuestions } from 'features/CVM88/Form/Form.view'
import { FormMode } from 'features/CVM88/Form/Form.types'

import BackButton from 'components/BackButton'
import { t, tO } from 'translate/i18n'
import Card from 'components/Card'
import Text from 'openspace/components/Text'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import Container from 'openspace/components/Container'
import Button from '@onepercentio/one-ui/components/Button'
import Step from 'features/CVM88/Form/Step'
import {
  DataStatus,
  StepStatus,
} from '@onepercentio/one-ui/components/Form/Form.types'
import { StepsWrapper } from 'features/CVM88/Form/Step/Step'
import { pick } from 'lodash'
import ActionsContainer from 'openspace/components/ActionsContainer'
import { PrerequirementsConfigs } from '@onepercentio/rarum-types/_shared'
import { RarumUserProfile } from 'core/logic/user/user.types'
import { Fragment, ReactElement, useEffect, useMemo } from 'react'
import { LPTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import IconRarum from 'components/IconRarum'
import Transitions from 'components/Transitions'
import { useDevelopmentError } from 'context/ErrorReporting'
import Loader from '@onepercentio/one-ui/components/Loader'
import ProgressDispatcher, { useProgressUI } from 'openspace/service/Progress'
import UncontrolledTransition from '@onepercentio/one-ui/components/UncontrolledTransition'
import { TransitionAnimationTypes } from '@onepercentio/one-ui/components/Transition'
export {
  ADDRESS_FORM_FIELDS,
  PERSONAL_DATA_FORM_FIELDS,
  REQUIRE_DATA_SHARE_FIELD,
} from 'context/FlowPrerequirements'

export function StandalonePrereqHeading({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <>
      <Text type='h2' color='primary'>
        {title}
      </Text>
      <Spacing size='16' />
      <Text type='h3heading'>{description}</Text>
    </>
  )
}

/**
 * Wraps a screen and display the prerequirements before rendering the page
 **/
export default function StandalonePrereqView<P extends PrereqStep>({
  steps,
  shouldRequestDataConsent,
  ...props
}: StandalonePrereqViewProps<P> &
  Pick<StandalonePrereqProps, 'shouldRequestDataConsent'> & {
    heading: ReactElement
  }) {
  const stepsWithInput = steps.filter((s) => s !== PrereqStep.KYC)
  const {
    isQuestionsAnswered,
    setShowErrors,
    questions,
    answers,
    onAnswerAction,
    sections,
    errors,
  } = useFormForStep(
    props.storedAnswers,
    props.currStep,
    props.currStep === steps[0] && shouldRequestDataConsent,
    {
      gallery: props.gallery,
      isRequired: true,
    }
  )

  const action = (
    <ActionsContainer>
      <Button
        variant='outline'
        onClick={() => {
          props.onCancel()
        }}>
        {props.currStep === steps[0]
          ? t(`generic.cancel`)
          : t('generic.goBack')}
      </Button>
      <Button
        variant='filled'
        color='primary'
        onClick={() => {
          if (!isQuestionsAnswered) setShowErrors(true)
          else props.onConfirm(pick(answers, ...questions.map((q) => q.id)))
        }}>
        {props.currStep === steps[steps.length - 1]
          ? t(`flowRequirements.standalone.to.${props.prereqTo}.action`)
          : t('generic.continue')}
      </Button>
    </ActionsContainer>
  )

  return (
    <>
      <Spacing size='40' />
      <Container>
        <BackButton onClick={() => props.onBack()} text={t('generic.goBack')} />
      </Container>
      <Spacing size='40' />

      <Container size={800}>
        <Card>
          {props.heading}
          {stepsWithInput.length > 1 && (
            <>
              <Spacing size='16' />
              <StepsWrapper
                currStep={steps.indexOf(props.currStep)}
                steps={steps.length}
                forceMobile>
                {steps.map((step, idx) => (
                  <Step
                    key={String(idx)}
                    dataStatus={DataStatus.PENDING}
                    stepName={tO(`flowRequirements.steps.${step}.title`)}
                    stepNumber={idx + 1}
                    stepStatus={
                      step === props.currStep
                        ? StepStatus.CURRENT
                        : StepStatus.COMPLETED
                    }
                  />
                ))}
              </StepsWrapper>
            </>
          )}
          <Spacing size='32' />
          <FormQuestions
            answers={answers}
            errors={errors}
            mode={FormMode.WRITE}
            onAnswer={(q, id, ans) => onAnswerAction(q as any, id, ans)}
            sections={sections as any}
            step={0}
            hideNonRequiredFields
          />
          {action}
        </Card>
      </Container>
    </>
  )
}

export function KYCView({
  method,
  profile,
  loading,
  error,
  ...actions
}: {
  method: PrerequirementsConfigs['kyc']
  profile: RarumUserProfile
  loading: boolean
  error: any
} & ClickActions<'onRetry' | 'onCancel' | 'onConfirmed' | 'onStarted'>) {
  const progressStatus = useProgressUI('kyc')
  const logDevError = useDevelopmentError()
  const profileKYCStatus = useMemo(() => {
    const methodUP = method.toUpperCase() as Uppercase<typeof method>
    return profile.kyc?.[methodUP]?.status
  }, [profile])
  const status = useMemo<
    | NonNullable<NonNullable<(typeof profile)['kyc']>['BRLA']>['status']
    | 'error'
  >(() => {
    if (loading) return 'verification_started'
    if (error) return 'error'
    return profileKYCStatus
  }, [profileKYCStatus, method, loading, error])

  useEffect(() => {
    switch (profileKYCStatus) {
      case 'verification_started':
      case 'awaiting_confirmation':
        ProgressDispatcher.dispatchHandle(
          'kyc',
          t('flowRequirements.steps.kyc.progress.waitingReturn'),
          1
        )
    }
  }, [profileKYCStatus])

  useEffect(() => {
    if (status === undefined) actions.onStarted()
  }, [])

  const content = (() => {
    switch (status) {
      case 'error':
        return (
          <Fragment key={'e'}>
            <IconRarum icon='alert' size={98} />
            <Text code='generic.errorMsg' type={LPTypo.H5} />
            <Spacing size='stretch' />
            <Button variant='filled' onClick={actions.onStarted}>
              {t('generic.retryBtn')}
            </Button>
            <Spacing size='8' />
            <Button variant='outline' onClick={actions.onCancel}>
              {t('generic.cancel')}
            </Button>
          </Fragment>
        )
      case 'verification_started':
      case 'awaiting_confirmation':
        return (
          <Fragment key={'l'}>
            <Loader size={12} />
            <Text code='flowRequirements.steps.kyc.loading' type={LPTypo.H5} />
            <Spacing size='16' />
            {progressStatus}
          </Fragment>
        )
      case 'rejected':
        return (
          <Fragment key={'r'}>
            <IconRarum icon='alert' size={98} />
            <Text
              code='flowRequirements.steps.kyc.rejected.title'
              type={LPTypo.H5}
            />
            <Text
              code='flowRequirements.steps.kyc.rejected.description'
              type={LPTypo.H3}
            />
            <Spacing size='stretch' />
            <Button variant='filled' onClick={actions.onRetry}>
              {t('generic.retryBtn')}
            </Button>
            <Spacing size='8' />
            <Button variant='outline' onClick={actions.onCancel}>
              {t('generic.cancel')}
            </Button>
          </Fragment>
        )
      case 'active':
        return (
          <Fragment key={'a'}>
            <IconRarum
              icon='roundCheckmark'
              size={98}
              color='var(--ok-color)'
            />
            <Text
              code='flowRequirements.steps.kyc.approved.title'
              type={LPTypo.H5}
            />
            <Text
              code='flowRequirements.steps.kyc.approved.description'
              type={LPTypo.H3}
            />
            <Spacing size='stretch' />
            <Button variant='filled' onClick={actions.onConfirmed}>
              {t('generic.continue')}
            </Button>
          </Fragment>
        )
      case undefined:
        return <Fragment key={'und'} />
      default:
        logDevError(
          `Layout not mapped for status ${status} at ${module.id}`,
          module
        )
        return <Fragment key={'und'} />
    }
  })()

  return <Transitions>{content}</Transitions>
}
